import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SideDrawer from "../SideDrawer";
import Button from "../Button";
import Chip from "../Chip";
import { askDelete, askGet, askPost, askPut, askUpload } from "../../utils/ask/askRequest";
import { newRequestId } from "../../utils/session";
import { OPEN_ASSISTANT_EVENT, OpenAssistantDetail, askPageFrom, openDraft } from "../../utils/assistant";
import { refreshAiAccess } from "../../hooks/useAiAccess";
import { useAiCapabilities } from "../../hooks/useAiCapabilities";
import { useAskProgress } from "../../hooks/useAskProgress";
import { AiChatDetail, AiChatMessage, AiChatSummary, MyAiAccess, AiFeedbackReason } from "../../types/Ai";
import { Cell } from "../../types/bd/Research";
import AskCapabilities from "./AskCapabilities";
import AskFigures from "./AskFigures";
import AskMaterialChip from "./AskMaterialChip";
import AskStart from "./AskStart";
import { useUserPermissions } from "../../contexts/UserPermissionsContext";
import { ATTACH_ACCEPT, AttachmentRead } from "../../utils/askAttachment";
import {
    ASK_MAX_QUESTION, ATTACH_DEFAULT, AttachAction, AttachGo, AttachInput, AttachPlan, AttachTarget, planAttachment, planFromRead, runAttachGo,
} from "../../utils/askAttachmentPlan";
import AiComposer from "../aiFill/AiComposer";
import classes from "./assistant.module.scss";
import { PastedKind, followUpActions, pastedActions, pastedKind, pastedNames, textForAi } from "../../utils/pastedBusiness";
import { pastedSource, recordAskFrom, recordAskReply } from "../../utils/pastedRecordRequest";
import {
    ASK_ACTION_EVENT, AskActionEvent, AskActionStatus, DISMISS_ASK_EVENT, RESUME_ASK_EVENT, askActionKey, cancelAskAction, savePatternFor, setAskWaiting,
    startAskAction, waitingNote,
} from "../../utils/askReturn";
import { useAskWaiting } from "../../hooks/useAskWaiting";
import { findSimilarRecord } from "../../utils/similarRecord";
import { commandReply, createCommand } from "../../utils/askCommands";
import { CreationSession, SessionList, advanceSession, canStartSession, describeSession, replyToSession, sessionKindAsked, startSession } from "../../utils/creationSession";
import { parseRecordIntent } from "../../utils/recordIntent";
import { lookUpPlace } from "../../utils/placeLookup";
import { wantsNewRecord } from "../../utils/askAttachment";
import { inlineWithLinks } from "../../utils/answerLinks";
import { FIGURE_MARK, downloadAnswerPdf, downloadAnswerWord, headingOf } from "../../utils/ask/askExport";
import { isNewTask, looksLikeAReply, requestOf, TASK_CHOICES } from "../../utils/ask/askReplyGuard";
import { toast } from "react-toastify";

/** Why an answer was Not right — one tap, counted, never any text. */
const FEEDBACK_REASONS: Array<{ value: AiFeedbackReason; label: string; title: string }> = [
    { value: "wrong_data", label: "Wrong data", title: "The facts in the answer were wrong or out of date" },
    { value: "misunderstood", label: "Didn't understand", title: "It answered a different question from the one asked" },
    { value: "missing_action", label: "Missing action", title: "It should have done something (filled a form, offered a button) and didn't" },
];

const PILL_SEEN_KEY = "crx:ask-pill-seen";
// the quick drawer's width (SideDrawer caps it to the viewport; phones are full width anyway); the full page has the room
const DRAWER_WIDTH = 560;
/** Source chips shown under an answer before "+N more". */
const MAX_SOURCES = 6;
/** The question sent with a paste too long to be one. */
const PASTE_QUESTION = "Use the pasted text to prepare what's needed.";
const isWebLink = (link: string): boolean => /^https?:\/\//i.test(link);
/** A source's label; a bare address shows as its site ("example.com"). */
const sourceLabel = (label: string): string => {
    if (!isWebLink(label)) return label;
    try {
        return new URL(label).hostname.replace(/^www\./, "");
    } catch {
        return label;
    }
};

/** POST ai/chats/ask. */
interface AskResponse {
    chatId: number;
    message: AiChatMessage;
    /** The question as saved (its real id and the file's details), so its file chip works at once. */
    userMessage?: AiChatMessage;
    /** With a file: what the server's read saw that CRX can act on (a booking, a receipt, a company document). */
    read?: AttachmentRead;
}

/** What happened to an action, on its button. */
const ACTION_STATE: Record<AskActionStatus, string> = {
    opened: "opened",
    filled: "✓ filled — save it",
    saved: "✓ saved",
    closed: "closed — nothing filled",
    skipped: "skipped",
};
// skipped counts as done: the person said they won't do it, so it never brings Ask back
const isDone = (status: AskActionStatus | undefined) => status === "filled" || status === "saved" || status === "skipped";
/** The keys of an answer's actions: its buttons, then its draft cards. */
const actionKeysOf = (message: AiChatMessage): string[] => [
    ...(message.localActions ?? []).map((_, at) => `${message.messageId}:a${at}`),
    ...(message.drafts ?? []).map((_, at) => `${message.messageId}:d${at}`),
];

/**
 * A file or pasted list Ask read but couldn't place yet (the next words can name where it goes), or
 * the creation conversation under way (the next words answer its question or change what it has).
 */
type Pending =
    | { type: "file"; target: NonNullable<AttachPlan["awaitsTarget"]>; turns: number; actions: AttachAction[] }
    | { type: "text"; text: string; kind: PastedKind; turns: number }
    | { type: "session"; session: CreationSession; turns: number };

/** What the reader saw, as context for a follow-up question (no names: the reader never reports them). */
const describeRead = (read: AttachmentRead): string =>
    [`${read.kind.replace(/_/g, " ")}${read.title ? `: ${read.title}` : ""}`, read.details].filter(Boolean).join("\n");

interface AssistantDrawerProps {
    access: MyAiAccess;
    /** "drawer" (the default): the quick drawer from any page. "page": only the thread and composer, for the full Ask CRX page. */
    mode?: "drawer" | "page";
    /** Page mode: the chat to show (loaded when it changes); null for the empty state. */
    chatId?: number | null;
    /** Page mode: a chat was created by the first ask (title null), or the mounted chat finished loading (its title). */
    onChatChange?: (chatId: number, title: string | null) => void;
    /** Page mode: after every answer or change worth a refetch of the rail (a figure edited, a file switch, a delete). */
    onChatUpdated?: () => void;
    /** Page mode: the record the conversation is about — sent as `context`, and as `linked` on the first ask of a new chat. */
    initialContext?: { kind: string; id: number } | null;
    /** Page mode: the linked record's name, shown as a quiet "About <name>" chip above the composer. */
    linkedLabel?: string | null;
}

const timeZone = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
};

/** Answer text → paragraphs and bullet lists (plain text only; React escapes it). */
const AnswerText: React.FC<{ text: string }> = ({ text }) => {
    const blocks: React.ReactNode[] = [];
    let bullets: string[] = [];
    const flush = (key: string) => {
        if (bullets.length) {
            blocks.push(<ul key={key} className={classes.list}>{bullets.map((item, i) => <li key={i}>{inlineWithLinks(item)}</li>)}</ul>);
            bullets = [];
        }
    };
    text.split(/\r?\n/).forEach((line, index) => {
        const trimmed = line.trim();
        // "[Figure 1]" marks where a table goes in the downloaded document; on screen the figures sit under the answer
        if (FIGURE_MARK.test(trimmed)) return;
        const bullet = /^([-•*]|\d+[.)])\s+(.*)$/.exec(trimmed);
        if (bullet) {
            bullets.push(bullet[2].replace(/\*\*/g, ""));
            return;
        }
        flush(`l${index}`);
        // a report's section titles ("## Method", "**Result**") read as headings, not as raw marks
        const heading = trimmed ? headingOf(trimmed) : null;
        if (heading) blocks.push(<p key={`h${index}`} className={classes.para}><strong>{heading.text}</strong></p>);
        else if (trimmed) blocks.push(<p key={`p${index}`} className={classes.para}>{inlineWithLinks(trimmed.replace(/\*\*/g, ""))}</p>);
    });
    flush("end");
    return <>{blocks}</>;
};

/**
 * The assistant (docs/ai-plan-2026-10-08.md A4): ask in plain words, get an
 * answer from what YOUR role can see, with links to the records it used.
 * Names are codes and money is a placeholder on the way to the AI; CRX puts
 * them back. "What the AI saw" shows exactly what was sent. Chats are
 * private and deleted after 30 days.
 */
const AssistantDrawer: React.FC<AssistantDrawerProps> = ({ access, mode: viewMode = "drawer", chatId: mountedChatId = null, onChatChange, onChatUpdated, initialContext = null, linkedLabel = null }) => {
    const isPage = viewMode === "page";
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    // An action opened something (a form, a Fill with AI drawer, a page): Ask shrinks to a pill instead of closing,
    // each action's button shows what happened, and Ask comes back when that answer still has something to do.
    const [isMinimized, setIsMinimized] = useState(false);
    const [actionStatus, setActionStatus] = useState<Record<string, AskActionStatus>>({});
    const [activeAction, setActiveAction] = useState<string | null>(null);
    // the answer Ask came back to after a save ("Saved — back where you left off")
    const [returnNote, setReturnNote] = useState<string | null>(null);
    // Hide, a new question or another chat: nothing waits any more, and a later save doesn't bring Ask back
    // Ask closed on purpose with steps left: only the header and search remind (no Save bar chip, no pill)
    const [isQuiet, setIsQuiet] = useState(false);
    const stopWaiting = () => {
        cancelAskAction();
        setActiveAction(null);
        setIsMinimized(false);
        setReturnNote(null);
        setIsQuiet(false);
    };
    const [question, setQuestion] = useState("");
    const [mode, setMode] = useState<"fast" | "smart">("fast");
    // "Ask again with Smart": this one question goes to Smart; the switch stays where it was
    const smartOnceRef = useRef(false);
    // a file or paste that came without words: it waits for "what should I do with it?" (no AI until the person says)
    const taskWaitRef = useRef<{ file: File } | { text: string } | null>(null);
    // the paste a chosen task goes with (read once by askChat)
    const pasteRef = useRef<string | null>(null);
    const [chatId, setChatId] = useState<number | null>(null);
    // the chat a step answered here goes into (read inside async steps, after earlier ones set it)
    const chatIdRef = useRef<number | null>(null);
    useEffect(() => { chatIdRef.current = chatId; }, [chatId]);
    const [messages, setMessages] = useState<AiChatMessage[]>([]);
    const [isAsking, setIsAsking] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [chats, setChats] = useState<AiChatSummary[] | null>(null);
    const [showHistory, setShowHistory] = useState(false);
    const [openSaw, setOpenSaw] = useState<number | null>(null);
    // the answer whose "More" (sources, what the AI saw) is open
    const [openMore, setOpenMore] = useState<number | null>(null);
    // the question in flight: the server reports the step it's on
    const [progressId, setProgressId] = useState<string | null>(null);
    const step = useAskProgress(progressId);
    // the answer whose source chips are all shown (past the first six)
    const [openSources, setOpenSources] = useState<number | null>(null);
    const endRef = useRef<HTMLDivElement>(null);
    // Copy an answer (#22 messages): the text on screen, real names and amounts in.
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const copyAnswer = async (message: AiChatMessage) => {
        try {
            await navigator.clipboard.writeText(message.text.split(/\r?\n/).filter((line) => !FIGURE_MARK.test(line.trim())).join("\n"));
            setCopiedId(message.messageId);
            window.setTimeout(() => setCopiedId((current) => (current === message.messageId ? null : current)), 2000);
        } catch {
            setCopiedId(null);
        }
    };
    // The question an answer replied to (the user's message just before it), for "Ask again with Smart".
    const askedBefore = (index: number): string => {
        for (let at = index - 1; at >= 0; at--) {
            const before = messages[at];
            if (before.role === "user") return /^Added: /.test(before.text ?? "") ? "" : (before.text ?? "").trim();
        }
        return "";
    };
    // Download one answer as a finished document (a report to send), titled with the conversation's name.
    const [downloadFor, setDownloadFor] = useState<number | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const downloadAnswer = async (message: AiChatMessage, kind: "word" | "pdf") => {
        setIsExporting(true);
        const current = chatIdRef.current;
        const detail = current ? await askGet<{ title: string | null }>(`ai/chats/${current}`).catch(() => null) : null;
        const title = (detail?.status === 200 && detail.body?.title) || "Ask CRX answer";
        try {
            await (kind === "word" ? downloadAnswerWord(title, message) : downloadAnswerPdf(title, message));
            setDownloadFor(null);
        } catch {
            toast.error("The document couldn't be made in this browser. Try the other format.");
        } finally {
            setIsExporting(false);
        }
    };
    // "Attach": a file CRX reads (code first, on this computer) to work out what it is and what to do.
    const { currentUser } = useUserPermissions() as any;
    const navigate = useNavigate();
    const [attachment, setAttachment] = useState<File | null>(null);
    // more files picked with the first (several at once): added one by one, then one question
    const [moreFiles, setMoreFiles] = useState<File[]>([]);
    // files marked public before sending (a published survey): their figures reach the AI from the first answer
    const [publicFiles, setPublicFiles] = useState<File[]>([]);
    const isPublicFile = (file: File) => publicFiles.includes(file);
    const [status, setStatus] = useState<string | null>(null);
    const pendingRef = useRef<Pending | null>(null);
    // The newest ask handler and drawer state, for the open event (subscribed once).
    const latest = useRef<{ ask: (text: string, fresh: boolean) => void; attach: (file: File, words: string, fresh: boolean) => void; wasOpen: boolean; busy: boolean; waitingId: string | null } | null>(null);
    const page = askPageFrom(location.pathname);
    const capabilities = useAiCapabilities(page?.kind ?? null, isPage || isOpen);
    // the answer to show when Ask opens again at a waiting answer
    const [scrollTo, setScrollTo] = useState<string | null>(null);

    useEffect(() => {
        // the full page is its own route: the open / resume events are the header drawer's
        if (isPage) return;
        const handleOpen = (event: Event) => {
            const detail = (event as CustomEvent<OpenAssistantDetail>).detail;
            const { ask, attach, wasOpen, busy, waitingId } = latest.current ?? { ask: null, attach: null, wasOpen: false, busy: false, waitingId: null };
            setIsOpen(true);
            // Ask waiting on an answer (2026-10-15): every way in goes back to that chat, and a question asked
            // from search joins it, so the steps left stay in view. A new chat is the drawer's New button.
            const fresh = !wasOpen && !waitingId;
            // A file with it ("Fill with AI" on a new request or mission): read and act on it like Attach.
            if (detail?.file) {
                if (detail.send && attach && !busy) attach(detail.file, detail.question ?? "", fresh);
                else { setAttachment(detail.file); if (detail.question) setQuestion(detail.question); }
                return;
            }
            if (!detail?.question) {
                if (waitingId) setScrollTo(waitingId);
                return;
            }
            // Asked from search: send it. A closed drawer starts a fresh chat, so an
            // old conversation's context doesn't ride along with a new topic.
            if (detail.send && ask && !busy) ask(detail.question, fresh);
            else setQuestion(detail.question);
        };
        // "Back to Ask" (the Save bar, the header, search): the same chat, at the waiting answer
        const handleResume = () => {
            setIsOpen(true);
            setScrollTo(latest.current?.waitingId ?? null);
        };
        window.addEventListener(OPEN_ASSISTANT_EVENT, handleOpen);
        // "Not now" on the Save bar chip: stop reminding; a later save doesn't bring Ask back
        const handleDismiss = () => {
            cancelAskAction();
            setActiveAction(null);
            setIsMinimized(false);
            setReturnNote(null);
            setIsQuiet(false);
        };
        window.addEventListener(RESUME_ASK_EVENT, handleResume);
        window.addEventListener(DISMISS_ASK_EVENT, handleDismiss);
        return () => {
            window.removeEventListener(OPEN_ASSISTANT_EVENT, handleOpen);
            window.removeEventListener(RESUME_ASK_EVENT, handleResume);
            window.removeEventListener(DISMISS_ASK_EVENT, handleDismiss);
        };
    }, [isPage]);

    // Opened again at a waiting answer: show that answer, not the end of the thread.
    useEffect(() => {
        if (!isOpen || !scrollTo) return;
        const timer = window.setTimeout(() => {
            const answer = document.querySelector(`[data-message-id="${scrollTo}"]`) as HTMLElement | null;
            answer?.scrollIntoView?.({ block: "start", behavior: "smooth" });
            setScrollTo(null);
        }, 60);
        return () => window.clearTimeout(timer);
    }, [isOpen, scrollTo]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages, isAsking]);

    const fetchChats = useCallback(async () => {
        const response = await askGet<AiChatSummary[]>("ai/chats").catch(() => null);
        setChats(response?.status === 200 && Array.isArray(response.body) ? response.body : []);
    }, []);

    const openChat = async (id: number) => {
        const response = await askGet<AiChatDetail>(`ai/chats/${id}`).catch(() => null);
        if (response?.status === 200 && response.body) {
            setChatId(response.body.chatId);
            setMessages(response.body.messages);
            setShowHistory(false);
            setError(null);
            pendingRef.current = null;
            stopWaiting();
            onChatChange?.(response.body.chatId, response.body.title);
        } else if (isPage) {
            setError("This conversation couldn't be opened. It may have been deleted.");
        }
    };

    // Page mode: the rail picks the chat. A chat this instance just created already matches, so it isn't reloaded.
    useEffect(() => {
        if (!isPage) return;
        if (mountedChatId === null) {
            if (chatIdRef.current !== null) startNew();
        } else if (mountedChatId !== chatIdRef.current) {
            openChat(mountedChatId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPage, mountedChatId]);

    const deleteChat = async (id: number) => {
        const response = await askDelete(`ai/chats/${id}`).catch(() => null);
        if (response?.status === 200) {
            setChats((previous) => (previous ?? []).filter((c) => c.chatId !== id));
            if (chatId === id) {
                setChatId(null);
                setMessages([]);
            }
            onChatUpdated?.();
        }
    };

    /** A table under an answer fixed by hand: saved on the server, then shown in place of the AI's. */
    const saveFigure = async (messageId: number, index: number, table: { columns: string[]; rows: Cell[][] }): Promise<boolean> => {
        if (chatId === null) return false;
        const response = await askPut<AiChatMessage>(`ai/chats/${chatId}/messages/${messageId}/figure`, { index, table }).catch(() => null);
        if (response?.status !== 200 || !response.body) return false;
        const saved = response.body;
        setMessages((previous) => previous.map((m) => (m.messageId === messageId ? { ...m, ...saved } : m)));
        onChatUpdated?.();
        return true;
    };

    /** Whether a file's amounts stay visible to the AI: shown at once, undone if it can't be saved. */
    const setPublicFigures = async (messageId: number, publicFigures: boolean): Promise<boolean> => {
        if (chatId === null) return false;
        const setFlag = (value: boolean) =>
            setMessages((previous) => previous.map((m) => (m.messageId === messageId && m.material ? { ...m, material: { ...m.material, publicFigures: value } } : m)));
        setFlag(publicFigures);
        const response = await askPut(`ai/chats/${chatId}/messages/${messageId}/material`, { publicFigures }).catch(() => null);
        if (response?.status !== 200) {
            setFlag(!publicFigures);
            return false;
        }
        onChatUpdated?.();
        return true;
    };

    // The person's own 👍/👎 on an answer; click again to clear. Shown at once, undone if it can't be saved.
    const handleFeedback = async (message: AiChatMessage, rating: "up" | "down") => {
        const next = message.feedback === rating ? null : rating;
        const setRating = (value: "up" | "down" | null | undefined) =>
            setMessages((previous) => previous.map((m) => (m.messageId === message.messageId ? { ...m, feedback: value } : m)));
        setRating(next);
        const response = await askPost(`ai/chats/messages/${message.messageId}/feedback`, { rating: next }).catch(() => null);
        if (response?.status !== 200) setRating(message.feedback);
    };

    // Why it was Not right: one tap, shown at once, undone if it can't be saved
    const handleReason = async (message: AiChatMessage, reason: AiFeedbackReason) => {
        if (message.feedbackReason === reason) return;
        const setReason = (value: AiFeedbackReason | null | undefined) =>
            setMessages((previous) => previous.map((m) => (m.messageId === message.messageId ? { ...m, feedback: "down", feedbackReason: value } : m)));
        setReason(reason);
        const response = await askPost(`ai/chats/messages/${message.messageId}/feedback`, { rating: "down", reason }).catch(() => null);
        if (response?.status !== 200) setReason(message.feedbackReason);
    };

    const startNew = () => {
        setChatId(null);
        setMessages([]);
        setError(null);
        setShowHistory(false);
        pendingRef.current = null;
        stopWaiting();
    };

    // What the attach plan needs to read a file and find where it goes (searches run as the person).
    const attachInput = (): Omit<AttachInput, "file" | "words"> => ({
        page, pages: currentUser?.pages ?? [], documentReading: !!access.dataRules?.documentReading,
        readOnServer: async (chosen, said) => {
            setStatus("Reading the file…");
            const form = new FormData();
            form.append("file", chosen);
            if (said) form.append("question", said);
            const response = await askUpload<AttachmentRead>("ai/attachments/read", form).catch(() => null);
            return response?.status === 200 && response.body ? { read: response.body } : { error: (response?.body as any)?.message ?? "Couldn't read that file. Please try again." };
        },
        findMission: async (reference): Promise<AttachTarget | null> => {
            const response = await askPost<any>("missions/search", { referenceNumber: reference, isActive: true, page: 1, pageSize: 5, isAdvanced: false }).catch(() => null);
            const rows: any[] = Array.isArray(response?.body) ? response!.body : response?.body?.data ?? [];
            const row = rows.find((r) => String(r.referenceNumber).toUpperCase() === reference);
            return row?.missionId ? { kind: "mission", id: Number(row.missionId), label: reference } : null;
        },
        findRecord: findSimilarRecord,
    });

    // the latest messages and statuses, for the action listener below
    const messagesRef = useRef<AiChatMessage[]>([]);
    useEffect(() => { messagesRef.current = messages; }, [messages]);
    const statusRef = useRef<Record<string, AskActionStatus>>({});
    useEffect(() => { statusRef.current = actionStatus; }, [actionStatus]);
    // opened any other way (search, Ctrl+K, a button): no pill
    useEffect(() => {
        if (!isOpen) return;
        setIsMinimized(false);
        setIsQuiet(false);
    }, [isOpen]);

    // What the action's form or drawer reports: shown on its button; Ask comes back to the answer when it was
    // saved or closed and something there is left to do (a closed one counts — choose again).
    useEffect(() => {
        const handle = (event: Event) => {
            const { key, status } = (event as CustomEvent<AskActionEvent>).detail ?? ({} as AskActionEvent);
            if (!key) return;
            const statuses = { ...statusRef.current, [key]: status };
            statusRef.current = statuses;
            setActionStatus(statuses);
            if (status !== "saved" && status !== "closed") return;
            const messageId = key.split(":")[0];
            const message = messagesRef.current.find((candidate) => String(candidate.messageId) === messageId);
            const left = message ? actionKeysOf(message).filter((other) => !isDone(statuses[other])) : [];
            if (left.length) {
                setIsOpen(true);
                setIsMinimized(false);
                setReturnNote(status === "saved" ? messageId : null);
                setScrollTo(messageId);
            } else if (status === "saved") {
                // every step done: nothing waits any more
                setActiveAction(null);
                setIsMinimized(false);
            }
        };
        window.addEventListener(ASK_ACTION_EVENT, handle);
        return () => window.removeEventListener(ASK_ACTION_EVENT, handle);
    }, []);

    /**
     * Run one of an answer's actions: Ask shrinks to the pill (never closes), the form or drawer opens, and what
     * happens there comes back to the action's button.
     * @param key - The action's key in the answer.
     * @param saves - The save request that completes it, when known.
     * @param run - Opens the form or drawer.
     */
    const runAction = (key: string, saves: RegExp | null, run: () => void) => {
        startAskAction(key, saves);
        setActiveAction(key);
        setIsMinimized(true);
        setIsQuiet(false);
        setIsOpen(false);
        setReturnNote(null);
        run();
    };

    /**
     * Skip beside a step not done yet, once its answer is under way (one of its steps was started); Undo beside a
     * skipped one. An untouched answer has nothing waiting, so no Skip.
     * @param message - The answer.
     * @param key - The step's key.
     * @param label - The step's button label, for screen readers.
     */
    const renderSkip = (message: AiChatMessage, key: string, label: string): React.ReactNode => {
        const status = actionStatus[key];
        if (status === "skipped") {
            return (
                <Button type="button" variant="ghost" size="xs" icon="arrow-counterclockwise" aria-label={`Undo skip: ${label}`}
                    title="Put this step back — it counts as left again" onClick={() => undoSkip(key)}>
                    Undo
                </Button>
            );
        }
        const isStarted = actionKeysOf(message).some((other) => !!actionStatus[other]);
        if (!isStarted || isDone(status)) return null;
        return (
            <Button type="button" variant="ghost" size="xs" icon="skip-forward" aria-label={`Skip: ${label}`}
                title="I won't do this one — Ask stops counting it and won't come back for it (Undo puts it back)" onClick={() => skipAction(key)}>
                Skip
            </Button>
        );
    };

    /** An answer's actions not done yet (filled, saved or skipped count as done). */
    const stepsLeftIn = (message: AiChatMessage): number => actionKeysOf(message).filter((key) => !isDone(actionStatus[key])).length;

    // What Ask waits on while minimised: the Save bar, the header and search show it; the pill only when no Save bar does.
    const waitingMessage = activeAction ? messages.find((candidate) => String(candidate.messageId) === activeAction.split(":")[0]) ?? null : null;
    const waitingLeft = waitingMessage ? actionKeysOf(waitingMessage).filter((key) => key !== activeAction && !isDone(actionStatus[key])).length : 0;
    const isWaitingFilled = !!activeAction && actionStatus[activeAction] === "filled";
    const waitingTitle = (() => {
        if (!waitingMessage) return null;
        const asked = messages.slice(0, messages.indexOf(waitingMessage)).reverse().find((candidate) => candidate.role === "user");
        const line = (asked?.text ?? "").split(/\r?\n/).map((part) => part.trim()).find(Boolean) ?? "";
        return line ? (line.length > 70 ? `${line.slice(0, 69)}…` : line) : null;
    })();
    const isWaiting = isMinimized && !isOpen && !!activeAction;
    const waitingId = isWaiting && activeAction ? activeAction.split(":")[0] : null;
    // the header's "waiting" reminder belongs to the drawer; the full page never drives it
    useEffect(() => {
        if (!isPage) setAskWaiting(isWaiting ? { title: waitingTitle, left: waitingLeft, filled: isWaitingFilled, quiet: isQuiet } : null);
    }, [isPage, isWaiting, waitingTitle, waitingLeft, isWaitingFilled, isQuiet]);
    useEffect(() => () => { if (!isPage) setAskWaiting(null); }, [isPage]);
    const { isHosted } = useAskWaiting();
    const showPill = !isPage && isWaiting && !isQuiet && !isHosted;

    // The pill pulses the first time anyone sees it, then never again (remembered per browser).
    const [isPulsing, setIsPulsing] = useState(false);
    useEffect(() => {
        if (!showPill) return;
        try {
            if (window.localStorage.getItem(PILL_SEEN_KEY) === "1") return;
            window.localStorage.setItem(PILL_SEEN_KEY, "1");
        } catch {
            return;
        }
        setIsPulsing(true);
    }, [showPill]);

    // Closed with steps left on an answer whose action was started: it keeps waiting, quietly — the header and
    // search still offer Back to Ask, but no chip or pill points at it (Not now, Skip or New stop it).
    const handleClose = () => {
        setIsOpen(false);
        if (activeAction && waitingMessage && (stepsLeftIn(waitingMessage) > 0 || isWaitingFilled)) {
            setIsMinimized(true);
            setIsQuiet(true);
        }
    };

    /**
     * Skip a step the person won't do: it counts as done, so it never brings Ask back and isn't counted as left.
     * @param key - The action's key in the answer.
     */
    const skipAction = (key: string) => {
        if (askActionKey() === key) cancelAskAction();
        setActionStatus((previous) => ({ ...previous, [key]: "skipped" }));
    };

    /** Undo a Skip: the step is to do again. */
    const undoSkip = (key: string) => {
        setActionStatus((previous) => {
            const next = { ...previous };
            delete next[key];
            return next;
        });
    };

    /**
     * Keep a step answered here (no AI, no credit) in the chat, so the thread is whole when it's reopened.
     * The AI never gets these messages — only the name-free note, as context for later questions.
     * @param asked - The person's words.
     * @param answer - What Ask answered here.
     * @param aiNote - One line about the step with no names (the server masks it again).
     * @param fresh - A new chat.
     */
    const keepLocal = async (asked: string, answer: string, aiNote: string, fresh = false) => {
        if (!asked.trim() || !answer.trim()) return;
        const response = await askPost<{ chatId: number }>("ai/chats/local", { chatId: fresh ? null : chatIdRef.current, question: asked, answer, aiNote }).catch(() => null);
        if (response?.status === 200 && response.body?.chatId) {
            const isNew = chatIdRef.current !== response.body.chatId;
            chatIdRef.current = response.body.chatId;
            setChatId(response.body.chatId);
            if (isNew) onChatChange?.(response.body.chatId, null);
            onChatUpdated?.();
        }
    };

    /**
     * The creation conversation's next step — its question, or what it has with the button that opens the
     * form filled in — read and answered here: no AI, no credit, the new names stay on this computer (D18).
     * @param asked - The person's words to show (null when they're on screen already, with the file).
     */
    const runSession = async (session: CreationSession, asked: string | null, fresh = false) => {
        setIsAsking(true);
        setStatus("Checking CRX…");
        const step = await advanceSession(session, { pages: currentUser?.pages ?? [], findRecord: findSimilarRecord, lookUpPlace }).catch(() => null);
        setIsAsking(false);
        setStatus(null);
        // it goes on while there's a question asked or a button to change before clicking
        pendingRef.current = step && (step.session.asked || step.actions.length) ? { type: "session", session: step.session, turns: 0 } : null;
        const now = Date.now();
        const at = new Date().toISOString();
        setQuestion("");
        if (fresh) { setChatId(null); setShowHistory(false); }
        setMessages((previous) => [...(fresh ? [] : previous),
            ...(asked !== null ? [{ messageId: -now, role: "user" as const, text: asked, createdAt: at }] : []),
            {
                messageId: -now - 1, role: "assistant" as const, text: step?.message ?? "Couldn't check CRX just now. Please try again.",
                localActions: step?.actions as any, replies: step?.replies, createdAt: at,
            },
        ]);
        if (step) {
            const where = step.session.asked ? `CRX asked for the ${step.session.asked === "ratesFor" ? "places the rates are for" : step.session.asked}.` : "CRX showed what it has with the button that opens the form.";
            await keepLocal(asked ?? "Attached a file", step.message, `${describeSession(step.session)} ${where}`, fresh);
        }
    };

    /** Words after a file or list Ask couldn't place: when they name where it goes, the buttons come back (no AI, no credit). */
    const answerFollowUp = async (asked: string, waiting: Pending): Promise<boolean> => {
        setIsAsking(true);
        let reply = "";
        let actions: AttachAction[] = [];
        if (waiting.type === "file") {
            const plan = await planFromRead(waiting.target.read, waiting.target.file, asked, attachInput(), false, waiting.target.localText).catch(() => null);
            // a new provider for it ("the provider is ABC and based in Oman"): the creation conversation
            if (plan?.session) {
                await runSession(startSession(parseRecordIntent(asked), plan.session), asked);
                return true;
            }
            if (plan?.targeted) {
                reply = plan.message;
                actions = plan.actions;
            }
        } else if (waiting.type === "text") {
            const intent = parseRecordIntent(asked);
            const list: SessionList | null = waiting.kind === "price_list" || waiting.kind === "fleet_list" ? { kind: waiting.kind, text: waiting.text } : null;
            const canStart = !!list && canStartSession("provider", currentUser?.pages ?? []);
            if (canStart && (wantsNewRecord(asked) || intent.base || intent.coverage.length)) {
                await runSession(startSession(intent, list), asked);
                return true;
            }
            actions = await followUpActions(waiting.text, waiting.kind, asked, currentUser?.pages ?? []).catch(() => []);
            reply = "Found it in CRX.";
            // a provider named that CRX doesn't have: start it, with the list
            if (!actions.length && canStart && intent.name) {
                await runSession(startSession(intent, list), asked);
                return true;
            }
        }
        setIsAsking(false);
        if (!actions.length) return false;
        const now = Date.now();
        setQuestion("");
        setMessages((previous) => [...previous,
            { messageId: -now, role: "user", text: asked, createdAt: new Date().toISOString() },
            { messageId: -now - 1, role: "assistant", text: reply, localActions: actions as any, createdAt: new Date().toISOString() },
        ]);
        await keepLocal(asked, reply, "CRX offered buttons that put the user's file or list in the right place in CRX.");
        return true;
    };

    const handleAsk = async (text?: string, fresh = false) => {
        if (attachment && moreFiles.length && text === undefined) {
            handleSeveral(question.trim(), fresh);
            return;
        }
        if (attachment && text === undefined) {
            handleAttachment(question.trim(), fresh);
            return;
        }
        const asked = (text ?? question).trim();
        if (!asked || isAsking) return;
        // a waiting helper takes only a reply that reads like its answer: a question or remark goes to the AI, and a new
        // task (a long brief, a full request) ends the helper altogether — helpers suggest, they never capture
        // the task for a file or paste that came without words: these words say what to do with it
        const task = fresh ? null : taskWaitRef.current;
        taskWaitRef.current = null;
        if (task && !isNewTask(asked)) {
            if ("file" in task) await askChat(asked, false, { file: task.file, material: true }, true);
            else {
                pasteRef.current = task.text;
                await askChat(asked, false);
            }
            return;
        }
        const held = fresh ? null : pendingRef.current;
        const isReply = looksLikeAReply(asked);
        if (held && (isNewTask(asked) || (held.type !== "session" && !isReply))) pendingRef.current = null;
        const waiting = fresh ? null : pendingRef.current;
        // the creation conversation: words about the record answer it or change it ("no, the name is ABC")
        if (waiting?.type === "session") {
            const next = isReply ? replyToSession(waiting.session, asked) : null;
            if (next) {
                await runSession(next, asked);
                return;
            }
            // a side question doesn't end it: it waits through up to three
            pendingRef.current = waiting.turns < 3 ? { ...waiting, turns: waiting.turns + 1 } : null;
        }
        // "create a new provider ABC, based in Oman": the creation conversation asks for what's missing, then the form
        const kindAsked = sessionKindAsked(asked);
        // a price or fleet list waiting for its provider: the new provider takes it (the follow-up below)
        const listWaits = waiting?.type === "text" || (waiting?.type === "file" && (waiting.target.read.kind === "price_list" || waiting.target.read.kind === "fleet_list"));
        if (kindAsked && !(kindAsked === "provider" && listWaits) && canStartSession(kindAsked, currentUser?.pages ?? [])) {
            // a list pasted with it is the new provider's: the request is its first line
            const pasted = kindAsked === "provider" ? pastedKind(asked) : null;
            const [request, ...rest] = asked.split(/\r?\n/);
            const listText = rest.join("\n").trim();
            const list: SessionList | null = (pasted === "price_list" || pasted === "fleet_list") && listText ? { kind: pasted, text: listText } : null;
            const intent = parseRecordIntent(list ? request : asked);
            await runSession(startSession({ ...intent, kind: kindAsked }, list), asked, fresh);
            return;
        }
        // "create new contact Sara Khoury +971…": read here, answered here — the new names never go to the AI
        const command = createCommand(asked);
        const commanded = command && !(command.kind === "provider" && listWaits) ? commandReply(command, currentUser?.pages ?? []) : null;
        if (commanded) {
            const now = Date.now();
            setQuestion("");
            if (fresh) { setChatId(null); setShowHistory(false); }
            setMessages((previous) => [...(fresh ? [] : previous),
                { messageId: -now, role: "user", text: asked, createdAt: new Date().toISOString() },
                { messageId: -now - 1, role: "assistant", text: commanded.message, localActions: commanded.actions as any, createdAt: new Date().toISOString() },
            ]);
            await keepLocal(asked, commanded.message, `The user asked CRX to open a new ${command!.kind} form from what they typed; CRX read it on their computer.`, fresh);
            return;
        }
        // "add Laila to our contacts" after a pasted email: the form opens filled in from it — read here, no AI, no credit
        const source = pastedSource(messages);
        const recordAsk = source ? recordAskFrom(asked, source) : null;
        if (source && recordAsk) {
            setIsAsking(true);
            const reply = await recordAskReply(recordAsk, source, currentUser?.pages ?? []).catch(() => null);
            setIsAsking(false);
            if (reply) {
                const now = Date.now();
                setQuestion("");
                setMessages((previous) => [...previous,
                    { messageId: -now, role: "user", text: asked, createdAt: new Date().toISOString() },
                    { messageId: -now - 1, role: "assistant", text: reply.message, localActions: reply.actions as any, createdAt: new Date().toISOString() },
                ]);
                await keepLocal(asked, reply.message, `The user asked CRX for a ${recordAsk.kind} from the message pasted earlier; CRX read it on their computer and offered the form filled in.`);
                return;
            }
        }
        if (waiting && waiting.type !== "session") {
            pendingRef.current = null;
            if (await answerFollowUp(asked, waiting)) return;
            // not where it goes: ask the AI, with what was read as context — and wait one more turn for a name
            if (waiting.turns < 1) pendingRef.current = { ...waiting, turns: waiting.turns + 1 };
            if (waiting.type === "file") {
                // the buttons offered with the file stay under the answer
                await askChat(asked, false, { file: waiting.target.file, details: describeRead(waiting.target.read) }, true, waiting.actions);
                return;
            }
        }
        // a question in the middle of the creation conversation: the AI gets what it's about — never the new names
        if (waiting?.type === "session" && waiting.session.list?.file) {
            await askChat(asked, false, { file: waiting.session.list.file, details: describeSession(waiting.session) }, true);
            return;
        }
        // a long paste with no request written at its top: ask what to do with it rather than guess
        const pastedAs = pastedKind(asked);
        if (textForAi(asked, pastedAs).length > ASK_MAX_QUESTION && !requestOf(asked)) {
            const now = Date.now();
            setQuestion("");
            if (fresh) { setChatId(null); chatIdRef.current = null; setShowHistory(false); }
            setMessages((previous) => [...(fresh ? [] : previous), {
                messageId: -now, role: "user", text: asked, createdAt: new Date().toISOString(),
                material: { name: "Pasted text", kind: "text", chars: asked.length, parts: `${asked.length.toLocaleString("en-US")} characters`, truncated: false },
            }]);
            const own = pastedAs ? await pastedActions(asked, pastedAs, location.pathname, currentUser?.pages ?? []).catch((): AttachAction[] => []) : [];
            offerTasks({ text: asked }, "the pasted text", own);
            return;
        }
        await askChat(asked, fresh);
    };

    /**
     * Ask the assistant (one message). With an attachment, the person's message
     * is already on screen and what CRX read from the file rides along — or, for
     * a document (material), the file itself goes with the question and the
     * server reads it whole, masked. A paste longer than a question goes the same way.
     */
    const askChat = async (asked: string, fresh: boolean, attached?: { file: File; details?: string; material?: boolean }, showAsked = !attached, carry: AttachAction[] = []) => {
        const sentMode: "fast" | "smart" = smartOnceRef.current ? "smart" : mode;
        smartOnceRef.current = false;
        setIsAsking(true);
        setStatus(null);
        setError(null);
        setQuestion("");
        if (fresh) {
            setChatId(null);
            setShowHistory(false);
        }
        // Pasted text read by code first: a price or fleet list, someone's details, a chat or email. A chat or
        // email goes without its stamps, sender names, header and signature; the names read here go beside it so
        // the server codes them (D18); the buttons under the answer read the real text on this computer.
        // a task chosen for a paste that came without words: the words are the question, the paste the material
        const pastedMaterial = !attached ? pasteRef.current : null;
        pasteRef.current = null;
        const kind = attached || pastedMaterial ? null : pastedKind(asked);
        const sent = textForAi(asked, kind);
        const names = kind ? pastedNames(asked) : null;
        const buttons = kind ? pastedActions(asked, kind, location.pathname, currentUser?.pages ?? []).catch((): AttachAction[] => []) : Promise.resolve([] as AttachAction[]);
        // longer than a question: the whole paste goes as material, read and masked on the server (its own buttons stay)
        const isLongPaste = !attached && (!!pastedMaterial || sent.length > ASK_MAX_QUESTION);
        const materialText = pastedMaterial ?? asked;
        // the request written at the top of a long paste is its question ("Can you prepare … based on the information provided?")
        const pasteQuestion = pastedMaterial ? asked : requestOf(asked) ?? PASTE_QUESTION;
        // the person sees what they pasted, in full; only the AI gets the trimmed version
        const trimmed = !isLongPaste && sent.replace(/\s+/g, " ").trim() !== asked.replace(/\s+/g, " ").trim();
        const pending: AiChatMessage = {
            messageId: -Date.now(), role: "user", text: asked, trimmed, attachment: showAsked ? undefined : attached?.file.name, createdAt: new Date().toISOString(),
            material: isLongPaste && !pastedMaterial ? { name: "Pasted text", kind: "text", chars: asked.length, parts: `${asked.length.toLocaleString("en-US")} characters`, truncated: false } : undefined,
        };
        if (showAsked) setMessages((previous) => (fresh ? [pending] : [...previous, pending]));
        const requestId = newRequestId();
        setProgressId(requestId);
        const context = initialContext ?? (page ? { kind: page.kind, id: page.id } : null);
        const isNewChat = fresh || chatIdRef.current === null;
        // the record a full-page conversation is about: linked once, when the chat is created
        const linked = isNewChat && initialContext ? { kind: initialContext.kind, id: initialContext.id } : undefined;
        const isMaterial = isLongPaste || !!attached?.material;
        let response: { status: number; body: AskResponse } | null;
        if (isMaterial) {
            const form = new FormData();
            form.append("question", isLongPaste ? pasteQuestion.slice(0, ASK_MAX_QUESTION) : asked);
            form.append("mode", sentMode);
            if (attached?.material && isPublicFile(attached.file)) form.append("publicFigures", "true");
            form.append("chatId", fresh || chatIdRef.current === null ? "" : String(chatIdRef.current));
            form.append("timeZone", timeZone());
            form.append("context", context ? JSON.stringify(context) : "");
            if (linked) form.append("linked", JSON.stringify(linked));
            form.append("requestId", requestId);
            if (attached?.material) {
                form.append("file", attached.file);
            } else {
                form.append("materialText", materialText);
                form.append("materialName", "Pasted text");
            }
            response = await askUpload<AskResponse>("ai/chats/ask", form).catch(() => null);
        } else {
            response = await askPost<AskResponse>("ai/chats/ask", {
                chatId: fresh ? null : chatIdRef.current, question: sent, shown: trimmed ? asked : undefined, mode: sentMode, timeZone: timeZone(), context, requestId, linked,
                attachment: attached ? { name: attached.file.name, details: attached.details } : undefined,
                // the "message" note promises buttons for its sender: only when there is one
                pasted: kind === "message" && !names?.sender.person && !names?.sender.company ? undefined : kind ?? undefined,
                names: names ? { people: names.people, companies: names.companies } : undefined,
                sender: names && (names.sender.person || names.sender.company) ? names.sender : undefined,
            }).catch(() => null);
        }
        setProgressId(null);
        if (response?.status === 200 && response.body) {
            const actions = [...(await buttons), ...carry];
            setIsAsking(false);
            setChatId(response.body.chatId);
            chatIdRef.current = response.body.chatId;
            if (isNewChat) onChatChange?.(response.body.chatId, null);
            // a price or fleet list whose provider isn't found: the next words can name it
            if ((kind === "price_list" || kind === "fleet_list") && !actions.some((action) => (action.go.type === "price_list_text" && action.go.recordId) || action.go.type === "fleet_text")) {
                pendingRef.current = { type: "text", text: asked, kind, turns: 0 };
            }
            const answer: AiChatMessage = { ...response.body.message };
            // the server's read saw something CRX acts on: the same buttons the photo reader gives, aimed at this
            // mission or the named record — no second read, no credit; its question ("whose is it?") waits for the next words
            if (attached?.material && response.body.read) {
                const plan = await planFromRead(response.body.read, attached.file, asked === ATTACH_DEFAULT ? "" : asked, attachInput(), false);
                if (plan.message) answer.text = [answer.text, plan.message].filter(Boolean).join("\n\n");
                if (plan.actions.length) answer.localActions = [...(answer.localActions ?? []), ...(plan.actions as any)];
                if (plan.awaitsTarget) pendingRef.current = { type: "file", target: plan.awaitsTarget, turns: 0, actions: plan.actions };
            }
            const saved = response.body.userMessage;
            setMessages((previous) => [
                // the question as the server saved it: its file chip (and the figures switch) works at once
                // (a file sent without words has its own bubble, made before the ask: match it by the file's name)
                ...(saved?.material ? previous.map((m) => (m.messageId === pending.messageId || (!showAsked && m.role === "user" && m.messageId < 0 && m.attachment === attached?.file.name)
                    ? { ...m, messageId: saved.messageId, material: saved.material } : m)) : previous),
                actions.length ? { ...answer, localActions: [...(answer.localActions ?? []), ...actions] as any } : answer,
            ]);
            refreshAiAccess();
            onChatUpdated?.();
        } else {
            setIsAsking(false);
            if (showAsked) setMessages((previous) => previous.filter((m) => m.messageId !== pending.messageId));
            setQuestion(asked === ATTACH_DEFAULT ? "" : asked);
            if (attached && !showAsked) setAttachment(attached.file);
            setError((response?.body as any)?.message ?? "The assistant couldn't answer. Please try again.");
            refreshAiAccess();
        }
    };

    /** An attached file: what it is (code first), then the next step to click — or a question to Ask. */
    // A file or paste with no words: "What should I do with it?" and one-tap choices, answered here — no AI, no credit
    // until the person picks one (or types their own). Its own buttons (a sender to add, say) stay under it.
    const offerTasks = (task: { file: File } | { text: string }, name: string, actions: AttachAction[] = []) => {
        taskWaitRef.current = task;
        const now = Date.now();
        setMessages((previous) => [...previous, {
            messageId: -now - 1, role: "assistant", text: `What should I do with ${name}? Pick one, or say it in your own words.`,
            replies: TASK_CHOICES, localActions: actions.length ? (actions as any) : undefined, createdAt: new Date().toISOString(),
        }]);
    };

    // Several files at once: each is added to the conversation (read whole, no AI, no credit), then one
    // question covers them all — the AI reads every file in the conversation.
    const handleSeveral = async (words: string, fresh: boolean) => {
        const files = [attachment, ...moreFiles].filter((file): file is File => !!file);
        if (!files.length || isAsking) return;
        setAttachment(null);
        setMoreFiles([]);
        setPublicFiles([]);
        setQuestion("");
        setError(null);
        setIsAsking(true);
        if (fresh) {
            setChatId(null);
            chatIdRef.current = null;
            setShowHistory(false);
            setMessages([]);
        }
        let current = fresh ? null : chatIdRef.current;
        const context = initialContext ?? (page ? { kind: page.kind, id: page.id } : null);
        for (let at = 0; at < files.length; at++) {
            const file = files[at];
            setStatus(`Reading ${file.name} (${at + 1} of ${files.length})…`);
            const form = new FormData();
            form.append("file", file);
            if (isPublicFile(file)) form.append("publicFigures", "true");
            if (current) form.append("chatId", String(current));
            if (context) form.append("context", JSON.stringify(context));
            if (!current && initialContext) form.append("linked", JSON.stringify({ kind: initialContext.kind, id: initialContext.id }));
            const response = await askUpload<{ chatId: number; userMessage: AiChatMessage }>("ai/chats/materials", form).catch(() => null);
            if (response?.status !== 200 || !response.body) {
                setStatus(null);
                setIsAsking(false);
                // what's left goes back in the box, so nothing has to be picked again
                setAttachment(files[at]);
                setMoreFiles(files.slice(at + 1));
                setQuestion(words);
                setError((response?.body as any)?.message ?? `${file.name} couldn't be read. Please try again.`);
                return;
            }
            const isNew = current !== response.body.chatId;
            current = response.body.chatId;
            chatIdRef.current = current;
            setChatId(current);
            if (isNew) onChatChange?.(current, null);
            const added = response.body.userMessage;
            setMessages((previous) => [...previous, added]);
        }
        setStatus(null);
        setIsAsking(false);
        onChatUpdated?.();
        await askChat(words || `Use the ${files.length} attached files to prepare what's needed.`, false);
    };

    const handleAttachment = async (words: string, fresh: boolean, given?: File) => {
        const file = given ?? attachment;
        if (!file || isAsking) return;
        setAttachment(null);
        setPublicFiles([]);
        setQuestion("");
        setError(null);
        setIsAsking(true);
        setStatus("Checking the file on this computer…");
        if (fresh) {
            setChatId(null);
            setShowHistory(false);
        }
        const sent: AiChatMessage = { messageId: -Date.now(), role: "user", text: words, attachment: file.name, createdAt: new Date().toISOString() };
        setMessages((previous) => (fresh ? [sent] : [...previous, sent]));
        pendingRef.current = null;
        const plan = await planAttachment({ file, words, ...attachInput() });
        const actions = plan.actions;
        // a document Ask can't act on itself: the file goes whole with the question; the server reads and masks it
        if (plan.material) {
            // no words with it: ask what to do (free), instead of guessing and spending a credit
            if (!words.trim()) {
                setIsAsking(false);
                setStatus(null);
                offerTasks({ file }, file.name);
                return;
            }
            await askChat(words.trim(), fresh, { file, material: true });
            return;
        }
        if (plan.chat) {
            await askChat(plan.chat.question, fresh, { file, details: plan.chat.details });
            return;
        }
        // a new provider for the list ("create new provider ABC, based in Oman"): the creation conversation
        if (plan.session) {
            if (plan.usedReader) refreshAiAccess();
            await runSession(startSession(parseRecordIntent(words), plan.session), null);
            return;
        }
        setIsAsking(false);
        setStatus(null);
        // the file leaves the box once sent; only a failed read keeps it there to try again
        if (plan.keepFile) setAttachment(file);
        if (plan.awaitsTarget) pendingRef.current = { type: "file", target: plan.awaitsTarget, turns: 0, actions };
        setMessages((previous) => [...previous, {
            messageId: -Date.now() - 1, role: "assistant", text: plan.message, localActions: actions as any, createdAt: new Date().toISOString(),
        }]);
        if (plan.usedReader) refreshAiAccess();
        await keepLocal(words || `Attached ${file.name}`, plan.message, "The user attached a file; CRX read it on their computer and offered the next steps.", fresh);
    };

    latest.current = {
        ask: (text, fresh) => { handleAsk(text, fresh); },
        attach: (file, words, fresh) => { handleAttachment(words.trim(), fresh, file); },
        wasOpen: isOpen,
        busy: isAsking,
        waitingId,
    };

    const fastLeft = access.fastPerDay === null ? null : Math.max(0, access.fastPerDay - access.usedFast);
    const canSend = (!!question.trim() || !!attachment) && !isAsking;
    const smartLeft = access.smartPerDay === null ? null : Math.max(0, (access.smartPerDay ?? 0) - access.usedSmart);
    const left = mode === "smart" ? smartLeft : fastLeft;

    const fillQuestion = (text: string) => {
        setQuestion(text);
        window.setTimeout(() => {
            const box = document.getElementById("assistant-question") as HTMLTextAreaElement | null;
            if (!box) return;
            box.focus();
            box.setSelectionRange(text.length, text.length);
        }, 0);
    };

    // the same conversation on the full page (its rail, files and downloads); a question not sent yet goes along
    const openFull = () => {
        setIsOpen(false);
        navigate(chatId === null ? "/ask" : `/ask/${chatId}`, { state: question.trim() ? { question } : undefined });
    };
    const headerExtra = (
        <div className={classes.headerExtra}>
            <Button type="button" variant="ghost" size="xs" icon="clock-history" title="Your earlier chats (kept 30 days, only you can see them)"
                onClick={() => { setShowHistory((v) => !v); if (!chats) fetchChats(); }}>
                Chats
            </Button>
            <Button type="button" variant="ghost" size="xs" icon="plus-lg" title="Start a new chat" onClick={startNew}>New</Button>
            <Button type="button" variant="ghost" size="xs" icon="arrows-fullscreen" onClick={openFull} title="Continue this conversation on the full Ask CRX page">
                Open full
            </Button>
        </div>
    );

    /** One answer's source chips, shown under it (six, then "+N more"), with the general-knowledge mark when it used none. */
    const renderSources = (message: AiChatMessage): React.ReactNode => {
        const sources = message.sources ?? [];
        if (!sources.length && !message.general) return null;
        const shown = openSources === message.messageId ? sources : sources.slice(0, MAX_SOURCES);
        return (
            <div className={classes.sources} aria-label="Sources">
                {shown.map((source) => (isWebLink(source.link) ? (
                    <a key={source.link} href={source.link} target="_blank" rel="noopener noreferrer" className={classes.source} title={`Open ${sourceLabel(source.label)} on the web`}>
                        <i className="bi bi-globe" aria-hidden="true" /> {sourceLabel(source.label)}
                    </a>
                ) : (
                    <Link key={source.link} to={source.link} className={classes.source} title={`Open ${source.label}`}>
                        <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> {source.label}
                    </Link>
                )))}
                {sources.length > shown.length && (
                    <button type="button" className={`btn-unstyled ${classes.source}`} onClick={() => setOpenSources(message.messageId)} title="Show every source this answer used">
                        +{sources.length - shown.length} more
                    </button>
                )}
                {message.general && (
                    <Chip tone="info" icon="stars" title="Not from CRX, your files or the web: the AI's own knowledge, which may be out of date.">General knowledge · check it</Chip>
                )}
            </div>
        );
    };

    const thread = (
                    <div className={classes.thread}>
                        {messages.length === 0 && (
                            <div className={classes.intro}>
                                {isPage ? <AskStart onPick={fillQuestion} /> : <p className={classes.para}>Ask in plain words about anything you can see in CRX. Here's what I can help you with:</p>}
                                <AskCapabilities map={isPage && capabilities ? { ...capabilities, onThisPage: [] } : capabilities} onAsk={(text) => handleAsk(text)} onFill={fillQuestion} />
                                <p className={classes.muted}>Names and amounts are hidden from the AI and put back here. Answers can be wrong: check the linked records.</p>
                            </div>
                        )}
                        {messages.map((message, index) => (
                            <div key={message.messageId} className={message.role === "user" ? classes.userMsg : classes.botMsg} data-message-id={message.messageId}>
                                {message.role === "user" && message.material && (
                                    <AskMaterialChip material={message.material} messageId={message.messageId}
                                        onPublicFigures={message.messageId > 0 ? (publicFigures) => setPublicFigures(message.messageId, publicFigures) : undefined} />
                                )}
                                {message.role === "user" && !message.material && message.attachment && (
                                    <span className={classes.fileChip} title="The file you attached"><i className="bi bi-paperclip" aria-hidden="true" /> {message.attachment}</span>
                                )}
                                {message.role === "user" ? (message.text ? <p className={classes.para}>{message.text}</p> : null) : <AnswerText text={message.text} />}
                                {message.role === "assistant" && (
                                    <AskFigures figures={message.figures} messageId={message.messageId}
                                        onEdit={message.messageId > 0 ? (index, table) => saveFigure(message.messageId, index, table) : undefined} />
                                )}
                                {message.role === "assistant" && renderSources(message)}
                                {message.role === "assistant" && message.suggestPublic !== undefined && (() => {
                                    // the file looks published (a survey, a public guide) and its figures are hidden: offer, never switch
                                    const asked = messages.find((m) => m.messageId === message.suggestPublic);
                                    if (!asked?.material || asked.material.publicFigures) return null;
                                    return (
                                        <p className={classes.returnNote} role="note">
                                            <i className="bi bi-eye" aria-hidden="true" /> {asked.material.name} looks like published material. Showing its figures to the AI makes comparisons and sums more accurate.{" "}
                                            <button type="button" className={`btn-unstyled ${classes.figuresToggle}`} onClick={() => setPublicFigures(asked.messageId, true)}
                                                title="Only for public material such as a published survey or a public guide; keep figures hidden for anything with our or a client's money. You can switch it back on the file.">
                                                Show its figures
                                            </button>
                                        </p>
                                    );
                                })()}
                                {message.role === "user" && message.trimmed && (
                                    <p className={classes.muted} title="Names, phones and emails in the header, signature and earlier replies never go to the AI; CRX's buttons read them here, on your computer.">
                                        <i className="bi bi-shield-lock" aria-hidden="true" /> Sent to the AI without the email header, signature and earlier replies — they stay on your computer.
                                    </p>
                                )}
                                {message.role === "assistant" && returnNote === String(message.messageId) && (
                                    <p className={classes.returnNote} role="status">
                                        <i className="bi bi-check2-circle" aria-hidden="true" /> Saved — back where you left off
                                        {stepsLeftIn(message) > 0 ? ` · ${stepsLeftIn(message)} step${stepsLeftIn(message) === 1 ? "" : "s"} left` : ""}
                                    </p>
                                )}
                                {message.role === "assistant" && (message.localActions ?? []).length > 0 && (
                                    <div className={classes.drafts}>
                                        {(message.localActions ?? []).map((action, at) => {
                                            const key = `${message.messageId}:a${at}`;
                                            const status = actionStatus[key];
                                            const isNext = !isDone(status) && actionKeysOf(message).some((other) => isDone(actionStatus[other]));
                                            return (
                                                <span key={action.label} className={classes.actionRow}>
                                                    <Button type="button" variant={isDone(status) ? "secondary" : "primary"} size="xs" icon={action.icon}
                                                        title={`${action.title}${status ? ` — ${ACTION_STATE[status]}` : ""}`}
                                                        onClick={() => runAction(key, savePatternFor(action.go as any), () => {
                                                            // the form opens with what the conversation gathered: it's done
                                                            if (pendingRef.current?.type === "session") pendingRef.current = null;
                                                            runAttachGo(action.go as AttachGo, navigate);
                                                        })}>
                                                        {isNext ? `Next: ${action.label}` : action.label}
                                                        {status && <span className={classes.actionState}>{ACTION_STATE[status]}</span>}
                                                    </Button>
                                                    {renderSkip(message, key, action.label)}
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                                {message.role === "assistant" && index === messages.length - 1 && !isAsking && (message.replies ?? []).length > 0 && (
                                    <div className={classes.next} aria-label="Quick answers">
                                        {(message.replies ?? []).map((reply) => (
                                            <button key={reply} type="button" className={`btn-unstyled ${classes.starter}`}
                                                onClick={() => handleAsk(reply)} title={`Answer: ${reply}`}>
                                                {reply}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {message.role === "assistant" && (message.drafts ?? []).length > 0 && (
                                    <div className={classes.drafts}>
                                        {(message.drafts ?? []).map((draft, at) => {
                                            const key = `${message.messageId}:d${at}`;
                                            const status = actionStatus[key];
                                            return (
                                            <div key={`${draft.kind}-${at}`} className={classes.draft}>
                                                <div className={classes.draftTitle}>
                                                    <i className="bi bi-pencil-square" aria-hidden="true" /> {draft.title}
                                                </div>
                                                {draft.warnings.length > 0 && (
                                                    <ul className={classes.draftWarnings}>
                                                        {draft.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                                                    </ul>
                                                )}
                                                <Button type="button" variant={isDone(status) ? "secondary" : "primary"} size="xs" icon="box-arrow-up-right"
                                                    onClick={() => runAction(key, savePatternFor({ kind: draft.kind, values: draft.values }), () => openDraft(draft))}
                                                    title="Open the form filled in: check it, change anything, then save. Nothing is saved until you do.">
                                                    Review and save
                                                    {status && <span className={classes.actionState}>{ACTION_STATE[status]}</span>}
                                                </Button>
                                                {renderSkip(message, key, draft.title)}
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {message.role === "assistant" && index === messages.length - 1 && !isAsking && message.messageId > 0 && access.smart && message.mode !== "smart" && askedBefore(index) && (
                                    <div className={classes.next}>
                                        <button type="button" className={`btn-unstyled ${classes.starter}`} disabled={smartLeft === 0}
                                            onClick={() => { smartOnceRef.current = true; askChat(askedBefore(index), false); }}
                                            title={smartLeft === 0 ? "No Smart answers left today" : `The same question, answered by the stronger model: better for a final report, a letter or several long files. Uses one Smart answer${smartLeft === null ? "" : ` (${smartLeft} left today)`}; the switch stays as it is.`}>
                                            <i className="bi bi-stars" aria-hidden="true" /> Ask again with Smart
                                        </button>
                                    </div>
                                )}
                                {message.role === "assistant" && index === messages.length - 1 && !isAsking && message.next
                                    && (message.next.questions.length > 0 || message.next.links.length > 0) && (
                                    <div className={classes.next} aria-label="What next">
                                        {message.next.questions.map((next) => (
                                            <button key={next.question} type="button" className={`btn-unstyled ${classes.starter}`}
                                                onClick={() => handleAsk(next.question)} title={`Ask: ${next.question}`}>
                                                {next.label}
                                            </button>
                                        ))}
                                        {message.next.links.map((next) => (
                                            <Link key={next.link} to={next.link} className={classes.source} title={`Open ${next.label}`}>
                                                <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> {next.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                                {message.role === "assistant" && message.messageId > 0 && (
                                    <div className={classes.feedback} role="group" aria-label="Answer actions">
                                        <button type="button" className={`btn-unstyled ${classes.feedbackButton}`} onClick={() => copyAnswer(message)}
                                            title="Copy this answer — names and amounts are already filled in. Paste it into an email or message yourself; Ask never sends anything.">
                                            <i className={`bi ${copiedId === message.messageId ? "bi-clipboard-check" : "bi-clipboard"}`} aria-hidden="true" /> {copiedId === message.messageId ? "Copied" : "Copy"}
                                        </button>
                                        <button type="button" className={`btn-unstyled ${classes.feedbackButton}`} aria-pressed={message.feedback === "up"}
                                            onClick={() => handleFeedback(message, "up")}
                                            title={message.feedback === "up" ? "Clear your rating" : "This answer was right and useful (only the rating is kept, never the text)"}>
                                            <i className={`bi ${message.feedback === "up" ? "bi-hand-thumbs-up-fill" : "bi-hand-thumbs-up"}`} aria-hidden="true" /> Helpful
                                        </button>
                                        <button type="button" className={`btn-unstyled ${classes.feedbackButton}`} aria-pressed={message.feedback === "down"}
                                            onClick={() => handleFeedback(message, "down")}
                                            title={message.feedback === "down" ? "Clear your rating" : "This answer was wrong or missed something (only the rating is kept, never the text)"}>
                                            <i className={`bi ${message.feedback === "down" ? "bi-hand-thumbs-down-fill" : "bi-hand-thumbs-down"}`} aria-hidden="true" /> Not right
                                        </button>
                                        {message.messageId > 0 && (
                                            <button type="button" className={`btn-unstyled ${classes.feedbackButton}`} aria-expanded={downloadFor === message.messageId}
                                                onClick={() => setDownloadFor(downloadFor === message.messageId ? null : message.messageId)}
                                                title="Download just this answer as a finished document, with its tables, charts and sources, named after the conversation">
                                                <i className="bi bi-download" aria-hidden="true" /> Download
                                            </button>
                                        )}
                                        {downloadFor === message.messageId && (
                                            <>
                                                <button type="button" className={`btn-unstyled ${classes.feedbackButton}`} disabled={isExporting} onClick={() => downloadAnswer(message, "word")}
                                                    title="A Word file you can edit before sending">
                                                    <i className="bi bi-file-earmark-word" aria-hidden="true" /> Word
                                                </button>
                                                <button type="button" className={`btn-unstyled ${classes.feedbackButton}`} disabled={isExporting} onClick={() => downloadAnswer(message, "pdf")}
                                                    title="A PDF ready to send">
                                                    <i className="bi bi-file-earmark-pdf" aria-hidden="true" /> PDF
                                                </button>
                                            </>
                                        )}
                                        {message.saw && (
                                            <button type="button" className={`btn-unstyled ${classes.feedbackButton}`} aria-expanded={openMore === message.messageId}
                                                onClick={() => setOpenMore(openMore === message.messageId ? null : message.messageId)}
                                                title="Exactly what the AI saw for this answer: names as codes, amounts as placeholders">
                                                <i className={`bi ${openMore === message.messageId ? "bi-chevron-up" : "bi-chevron-down"}`} aria-hidden="true" /> {openMore === message.messageId ? "Less" : "More"}
                                            </button>
                                        )}
                                        {message.feedback === "down" && (
                                            <div className={classes.reasons} role="group" aria-label="What was wrong?">
                                                <span className={classes.muted}>What was wrong?</span>
                                                {FEEDBACK_REASONS.map((reason) => (
                                                    <button key={reason.value} type="button" className={`btn-unstyled ${classes.reasonChip}`} aria-pressed={message.feedbackReason === reason.value}
                                                        onClick={() => handleReason(message, reason.value)} title={`${reason.title} (only this choice is kept, never the text)`}>
                                                        {reason.label}
                                                    </button>
                                                ))}
                                                {message.feedbackReason && <span className={classes.muted}>Thanks — that tells us what to fix.</span>}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {message.role === "assistant" && openMore === message.messageId && message.saw && (
                                    <div className={classes.sawWrap}>
                                        <button type="button" className={`btn-unstyled ${classes.sawToggle}`}
                                            onClick={() => setOpenSaw(openSaw === message.messageId ? null : message.messageId)}
                                            title="Exactly what was sent to the AI for this answer: names as codes, amounts as placeholders">
                                            <i className={`bi ${openSaw === message.messageId ? "bi-eye-slash" : "bi-eye"}`} aria-hidden="true" /> What the AI saw
                                        </button>
                                        {openSaw === message.messageId && (
                                            <div className={classes.saw}>
                                                <div className={classes.sawLabel}>Your question, as sent</div>
                                                <pre className={classes.code}>{message.saw.question}</pre>
                                                {message.saw.tools.map((tool, index) => (
                                                    <div key={index}>
                                                        <div className={classes.sawLabel}>Lookup: {tool.name} {JSON.stringify(tool.input)}</div>
                                                        <pre className={classes.code}>{tool.output}</pre>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                        {isAsking && <div className={classes.botMsg}><p className={classes.muted}><i className="bi bi-hourglass-split" aria-hidden="true" /> {status ?? step ?? "Looking it up…"}</p></div>}
                        {error && <div className={classes.error} role="alert">{error}</div>}
                        <div ref={endRef} />
                    </div>
    );

    const composer = (
                <div className={classes.composer}>
                    {isPage && linkedLabel && (
                        <div className={classes.linked}>
                            <Chip tone="neutral" icon="link-45deg" title="The record this conversation is about: the AI can use what you can see of it">About {linkedLabel}</Chip>
                        </div>
                    )}
                    <AiComposer
                        id="assistant-question"
                        label={attachment ? "Say what to do with it (optional)" : "Ask anything, or attach a file"}
                        text={question}
                        onTextChange={setQuestion}
                        rows={isPage ? 3 : 2}
                        nativeProps={{ enterKeyHint: "send" }}
                        file={attachment}
                        onFileChange={(chosen) => {
                            // removing the first of several files moves the next one up
                            if (!chosen && moreFiles.length) { setAttachment(moreFiles[0]); setMoreFiles(moreFiles.slice(1)); return; }
                            setAttachment(chosen);
                            if (chosen) setError(null);
                        }}
                        moreFiles={moreFiles}
                        onMoreFiles={setMoreFiles}
                        fileExtra={(file) => {
                            const isOn = isPublicFile(file);
                            return (
                                <button type="button" className={`btn-unstyled ${classes.figuresToggle} ${isOn ? classes.figuresOn : ""}`} aria-pressed={isOn}
                                    onClick={() => setPublicFiles((previous) => (isOn ? previous.filter((f) => f !== file) : [...previous, file]))}
                                    title={`${isOn ? "Click to hide" : "Click to show"} this file's figures to the AI. Public material such as a published survey: its amounts stay visible so the AI can compare them. Keep them hidden for anything with our or a client's money.`}>
                                    <i className={`bi ${isOn ? "bi-eye" : "bi-eye-slash"}`} aria-hidden="true" /> {isOn ? "Figures visible to the AI" : "Figures hidden from the AI"}
                                </button>
                            );
                        }}
                        accept={ATTACH_ACCEPT}
                        attachTitle="Attach a file — a price list, booking, client brief or itinerary, business card, contract or permit. CRX works out what it is and what to do; you check before anything is saved. You can also drop or paste a file here."
                        submitLabel="Ask"
                        busyLabel="Asking…"
                        submitIcon="send"
                        submitTitle={attachment ? "Send the file (and your words)" : "Send the question"}
                        isBusy={isAsking}
                        hasOwnProgress
                        canSubmit={canSend && left !== 0}
                        onSubmit={() => handleAsk()}
                        usesAi
                        aiAccess={access}
                        aiMode={mode}
                        submitOnEnter
                        tools={access.smart ? (
                            <div className={classes.modes} role="group" aria-label="Model">
                                <button type="button" className={`btn-unstyled ${classes.mode} ${mode === "fast" ? classes.modeOn : ""}`} aria-pressed={mode === "fast"}
                                    onClick={() => setMode("fast")} title={`Fast${access.fastModel ? ` (${access.fastModel})` : ""}: quick and cheap, right for most questions, lookups, tables and sums`}>Fast</button>
                                <button type="button" className={`btn-unstyled ${classes.mode} ${mode === "smart" ? classes.modeOn : ""}`} aria-pressed={mode === "smart"}
                                    onClick={() => setMode("smart")} title={`Smart${access.smartModel ? ` (${access.smartModel})` : ""}: stronger reasoning; pick it for a final report or letter, or to compare several long files${smartLeft === null ? "" : ` (${smartLeft} left today)`}`}>Smart</button>
                            </div>
                        ) : undefined}
                        footnote="Private to you · deleted after 30 days · Enter sends, Shift+Enter adds a line"
                    />
                </div>
    );

    if (isPage) {
        return <div className={classes.page}>{thread}{composer}</div>;
    }

    return (
        <>
        {showPill && (
            <div className={`${classes.pill} ${isPulsing ? classes.pillPulse : ""}`} role="status" aria-label="Ask CRX is waiting">
                <button type="button" className={`btn-unstyled ${classes.pillOpen}`} onClick={() => { setIsOpen(true); setScrollTo(waitingId); }}
                    title="Back to the answer — the same chat; each action's button shows what happened">
                    <i className="bi bi-stars" aria-hidden="true" /> <span className={classes.pillTitle}>Back to Ask</span>
                    <span className={classes.pillNote}>· {waitingNote({ title: waitingTitle, left: waitingLeft, filled: isWaitingFilled, quiet: isQuiet })}</span>
                </button>
                <button type="button" className={`btn-unstyled ${classes.pillHide}`} onClick={stopWaiting}
                    title="Stop reminding me — saving won't bring Ask back. The chat stays in Ask's Chats.">
                    Not now
                </button>
            </div>
        )}
        <SideDrawer open={isOpen} title="Ask CRX" onClose={handleClose} width={DRAWER_WIDTH} headerExtra={headerExtra}>
            <div className={classes.drawer}>
                {showHistory ? (
                    <div className={classes.history}>
                        {chats === null && <p className={classes.muted}>Loading…</p>}
                        {chats !== null && chats.length === 0 && <p className={classes.muted}>No earlier chats.</p>}
                        {(chats ?? []).map((chat) => (
                            <div key={chat.chatId} className={classes.historyRow}>
                                <button type="button" className={`btn-unstyled ${classes.historyOpen}`} onClick={() => openChat(chat.chatId)} title="Open this chat">
                                    <span className={classes.historyTitle}>{chat.title ?? "Chat"}</span>
                                    <span className={classes.muted}>{new Date(chat.updatedAt).toLocaleString()}</span>
                                </button>
                                <Button type="button" variant="ghost" size="xs" icon="trash" title="Delete this chat" onClick={() => deleteChat(chat.chatId)}>Delete</Button>
                            </div>
                        ))}
                    </div>
                ) : thread}
                {composer}
            </div>
        </SideDrawer>
        </>
    );
};

export default AssistantDrawer;
